/**
 * What holding a non-USD asset has cost, in dollars, purely because the currency moved.
 *
 * WHY THIS EXISTS
 * The app values everything at spot and reports one USD total. That is correct and it is the
 * point of the product. But it makes two very different things look identical in a
 * reconciliation:
 *
 *   - money that is missing            (a data error, to be hunted)
 *   - money that is still here and is now worth fewer dollars   (an FX loss, to be explained)
 *
 * A ₺474,000 deposit that was $11,000 when it was opened is $10,000 at 47.4. Nothing was
 * lost. A thousand dollars of purchasing power was. The app had no way to say that, so it
 * surfaced as an unexplained residual — and an unexplained residual is what stops every
 * return figure from being printed. (audit 2026-08-02)
 *
 * THE HARD PART IS NOT THE ARITHMETIC, IT IS THE DATE.
 * A cost basis with no date has no "rate at purchase", so its FX effect is not small or
 * approximate — it is UNCOMPUTABLE. Live state carries 27 holdings whose entire basis is a
 * bare `avgCost` with no lot and no monthly entry. Those are reported by name in `undated`
 * and excluded from the total, never averaged in at spot. Assuming spot would silently
 * report their FX effect as exactly zero, which is the most flattering possible answer and
 * the least defensible.
 *
 * NO IMPORTS, NO STATE WRITES, NO NETWORK. Rates are injected: `rateAt(dateISO, currency)`
 * returns USD per unit of that currency on that date, or null when the app has no record —
 * and null means uncomputable, not "use today's".
 */

/** One cost lump with a date attached, ready to be priced at two instants. */
function _item(label, currency, costLocal, dateISO) {
  return { label, currency, costLocal, date: dateISO };
}

/**
 * Every dated cost lump the app can prove, plus the ones it cannot.
 * Split out from the summing so the date logic is readable and testable on its own.
 */
export function datedCostBasis(S) {
  const dated = [], undated = [];
  const s = S || {};
  // The DECLARED opening month. Positions the owner brought into the app as a single
  // averaged lump have no purchase history and never will — the trades happened before the
  // app existed, across months he cannot reconstruct. That is not missing data, it is the
  // edge of the record. What it does have is a date: the month he entered it.
  //
  // He declares that month once, in Settings. The app does not infer it, and every figure
  // derived from it is marked `declared` so it can never be mistaken for a measured
  // purchase date. Unset means unset: those positions stay uncomputable rather than being
  // dated by a guess. (audit 2026-08-02)
  const basis = (s.settings && s.settings.basisMonth) || null;
  const basisDate = /^\d{4}-\d{2}$/.test(basis || '') ? basis + '-15' : null;
  // A per-position date always beats the portfolio-wide one. The owner reconstructed the
  // ten largest opening lumps from his own records on 2026-08-03 and nine fell in January
  // 2026 — but TEJ was 2026-02-06, and a month-wide default would have priced it at
  // January's rate. The whole reason for allowing an override is that the average is right
  // about the portfolio and wrong about a position. (audit 2026-08-03)
  const dateFor = h => {
    const d = h && h.basisDate;
    return /^\d{4}-\d{2}(-\d{2})?$/.test(d || '') ? (d.length === 7 ? d + '-15' : d) : basisDate;
  };

  const monthsFor = ticker => {
    const out = [];
    Object.entries(s.monthlyRates || {}).forEach(([m, r]) => {
      const e = r && r.entries && r.entries[ticker];
      // A LOT-SOURCED ENTRY IS THE SAME PURCHASE THE LOT LOOP BELOW ALREADY DATED.
      //
      // This basis is additive by design — lots, monthly entries and one opening lump can all
      // be true at once, and `rest = total - logged - lotCost` subtracts each exactly once.
      // The month echo written by `saveMo` for a row bought through `+ Lot` is not a fourth
      // source; it is the lot restated. Counting it would subtract that cost twice and drive
      // `rest` negative, which fails the `rest > 0.005` guard and deletes the opening lump
      // from the currency analysis without a word. (audit 2026-09-03)
      if (e && e.source === 'lots') return;
      const local = e ? (e.qty || 0) * (e.price || 0) : 0;
      if (local > 0) out.push({ month: m, local });
    });
    return out.sort((a, b) => a.month.localeCompare(b.month));
  };

  (s.holdings || []).forEach(h => {
    if (!h || h.type === 'Cash') return;
    const cur = h.currency || 'USD';
    // LOTS ARE ADDITIVE PROVENANCE, NOT AN ALL-OR-NOTHING BASIS.
    //
    // This used to be `if (lots.length) { ...; return; }` — one lot and the monthly entries
    // and the opening lump both vanished from the FX calculation. It is the same defect
    // math.js carried until 2026-08-03, pointed at a different file, and TEJ is what
    // exposed it: recording its Akbank purchase (TRY 10,630, 2025-06-11) as a dated lot
    // would have deleted TRY 19,402 of monthly entries and TRY 18,008 of opening lump from
    // the basis, leaving a TRY 48,041 position priced as if it were TRY 10,630.
    //
    // A position can legitimately hold all three at once: dated lots for purchases the
    // owner reconstructed, monthly entries for the disciplined DCA, and one undated lump
    // for what predates the app. Each is subtracted from the total exactly once.
    const lots = (h.lots || []).filter(l => l && l.type !== 'drip');
    const lotCost = lots.reduce((a, l) => a + (l.cost || (l.qty || 0) * (l.price || 0)), 0);
    lots.forEach(l => dated.push(_item(h.ticker, cur, l.cost || (l.qty || 0) * (l.price || 0), l.date)));
    const months = monthsFor(h.ticker);
    const total = (h.qty || 0) * (h.avgCost || 0);
    // Everything the monthly log DOES cover is dated by its own month — those are the
    // disciplined monthly purchases, and each one has a real rate behind it.
    const logged = months.reduce((a, m) => a + m.local, 0);
    months.forEach(m => dated.push(_item(h.ticker, cur, m.local, m.month + '-15')));
    // What is left is the opening lump: bought before the app existed, entered as one
    // average. Dated at the declared opening month if there is one — and NEVER by smearing
    // the logged months' rates across it, which would look precise and be fiction.
    const rest = total - logged - lotCost;
    if (rest > 0.005) {
      const d = dateFor(h);
      if (d) dated.push({ ..._item(h.ticker, cur, rest, d), declared: true, exact: !!h.basisDate });
      else undated.push({ ticker: h.ticker, currency: cur, costLocal: rest,
        reason: months.length ? 'opening lump, no declared opening month' : 'no lot, no monthly entry, no declared opening month' });
    }
  });

  (s.trades || []).forEach(t => {
    // CONTRACT-001's second instance, found by the sweep written for the first (rule #25).
    // A partially exited trade still holds its remainder at `entry`, and that cost has an FX
    // basis like any other. Filtering it out here left the currency-drift analysis blind to it,
    // which is the same one-sided omission the assets side had.
    if (!t || t.status === 'Closed') return;
    const local = (t.entry || 0) * (t.qty || 0);
    if (!(local > 0)) return;
    if (t.openDate) dated.push(_item(t.ticker, t.currency || 'USD', local, t.openDate));
    else undated.push({ ticker: t.ticker, currency: t.currency || 'USD', costLocal: local, reason: 'open trade with no open date' });
  });

  (s.rfTxs || []).forEach(t => {
    if (!t || t.type === 'withdraw') return;
    const local = Math.abs(t.amount || 0);
    if (!(local > 0)) return;
    if (t.date) dated.push(_item('Reserve fund', t.currency || 'TRY', local, t.date));
    else undated.push({ ticker: 'Reserve fund', currency: t.currency || 'TRY', costLocal: local, reason: 'reserve deposit with no date' });
  });

  // Cash balances are deliberately absent. A balance is not a purchase: it has no single
  // date, and inventing one would put a made-up number in the only line here that exists to
  // separate what is known from what is not.
  return { dated, undated };
}

/**
 * @param {Object} S
 * @param {Object} ctx  { toUSD(v, cur), rateAt(dateISO, cur) -> USD per unit, or null }
 * @returns {{effectUSD:number, atPurchaseUSD:number, atSpotUSD:number,
 *            undatedCostUSD:number, undated:Array, byCurrency:Object, coveragePct:number}}
 *   effectUSD is NEGATIVE when the currency fell — the dollars the position lost by being
 *   denominated in something other than USD.
 */
export function fxEffectOnCost(S, ctx) {
  const c = ctx || {};
  const now = typeof c.toUSD === 'function' ? c.toUSD : (v => v);
  const rateAt = typeof c.rateAt === 'function' ? c.rateAt : (() => null);

  const { dated, undated } = datedCostBasis(S);
  let atPurchase = 0, atSpot = 0, declaredSpot = 0;
  const byCurrency = {};
  const stillUndated = undated.slice();

  dated.forEach(it => {
    const spotUSD = now(it.costLocal, it.currency);
    if (it.currency === 'USD') { atPurchase += spotUSD; atSpot += spotUSD; return; }
    const r = rateAt(it.date, it.currency);
    if (!(r > 0) || !isFinite(spotUSD)) {
      // The app has no rate on record for that date. Not an approximation problem — there is
      // no answer, so it joins the undated pile instead of quietly scoring zero effect.
      stillUndated.push({ ticker: it.label, currency: it.currency, costLocal: it.costLocal, reason: 'no recorded rate for ' + String(it.date).slice(0, 10) });
      return;
    }
    const purchaseUSD = it.costLocal * r;
    atPurchase += purchaseUSD;
    atSpot += spotUSD;
    if (it.declared) declaredSpot += spotUSD;
    const b = byCurrency[it.currency] = byCurrency[it.currency] || { atPurchaseUSD: 0, atSpotUSD: 0, effectUSD: 0, declaredUSD: 0 };
    b.atPurchaseUSD += purchaseUSD;
    b.atSpotUSD += spotUSD;
    b.effectUSD += spotUSD - purchaseUSD;
    if (it.declared) b.declaredUSD += spotUSD;
  });

  const undatedCostUSD = stillUndated.reduce((a, u) => {
    const v = now(u.costLocal, u.currency);
    return a + (isFinite(v) ? v : 0);
  }, 0);
  const covered = atSpot + undatedCostUSD;

  return {
    effectUSD: atSpot - atPurchase,
    atPurchaseUSD: atPurchase,
    atSpotUSD: atSpot,
    // How much of the covered basis rests on the owner's declared opening month rather than
    // on a recorded purchase. Reported separately so the figure can always be qualified —
    // "(declared)" is not a footnote, it is part of the number's meaning.
    declaredUSD: declaredSpot,
    basisMonth: (S && S.settings && S.settings.basisMonth) || null,
    undatedCostUSD,
    undated: stillUndated.sort((a, b) => (b.costLocal || 0) - (a.costLocal || 0)),
    byCurrency,
    coveragePct: covered > 0 ? (atSpot / covered) * 100 : 0,
    declaredPct: atSpot > 0 ? (declaredSpot / atSpot) * 100 : 0,
  };
}
